import {fromNullable} from 'data.maybe';
import {Model, Collection, View, ModelFetchOptions, CollectionFetchOptions} from 'backbone';
import {bindAll} from 'lodash';
import {all} from 'ramda';
import _ from 'underscore';

/**
 * Cleans up backbone views, including additional subviews, which are not part of the base View class.
 * Then unbind the view's events.
 * Then call the view's own destroy method, if it has one.
 *
 * @param view the view to clean up
 * @returns the view that was cleaned up
 */
export const destroy = <T extends View<any>>(view: T & {subViews?: []; destroy?() : void }): T => {
    // recursively call this method on all subviews, unbinding events as we come back up to the initial view
    if (view.subViews && view.subViews.length) _.each(view.subViews, destroy);

    // remove any data attributes from the view's dom element
    view.$el.removeData().unbind();

    // remove events that were created in the "events" object defined in the view
    view.undelegateEvents()
        .unbind()
        .remove();

    // remove all listeners that this view has set on other objects (most likely our eventChannel system)
    view.stopListening();

    // call the specific view's destroy method, if it has one
    if (view.destroy && view.destroy.constructor === Function) view.destroy();

    // not sure anything calling this method ever uses the return value, but this was pre-existing
    return view;
};

// allPassed :: [Boolean] -> Boolean
export const allPassed = all(Boolean);

export const bindAllMethods = (view: View<any>) => {
    const methods = Object.keys(view.constructor.prototype).filter(key => typeof view[key] === 'function');
    bindAll(view, methods);
    return view;
};

export const maybeFind = (pred: (x: any) => boolean) => (collection: Collection<Model>) =>
    fromNullable(collection.find(pred));

export const findAll = (selector: string) => <T extends View<any>>(view: T) =>
    Array.from(view.el.querySelectorAll(selector));

export const maybeProp = (key: string) => (model: Model) => fromNullable(model.get(key));

// Compare Model attribute against value
export const propEq = (propName: string, val: any) => (model: Model) => model.get(propName) === val;

// ES2015 Promisified fetch
export const fetch = <T extends Model | Collection<any>>(
    model: T,
    opts: ModelFetchOptions | CollectionFetchOptions,
): Promise<unknown> =>
        new Promise((res, rej) =>
            model
                .fetch(opts)
                .then(res)
                .fail(rej),
        );
